import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { ethers } from 'ethers';
import { clearUserToken } from '../utils/api';

const WalletContext = createContext(null);

export const useWallet = () => useContext(WalletContext);

export const WalletProvider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // ─── EIP-6963 multi-wallet discovery ───
  // When a user has more than one wallet extension installed (MetaMask, Trust,
  // Coinbase, OKX, …) window.ethereum is whichever one won the race. EIP-6963
  // lets every wallet announce itself so we can ask the user which to use
  // instead of silently opening one.
  const [wallets, setWallets] = useState([]);          // [{ info, provider }]
  const [activeEth, setActiveEth] = useState(null);     // the chosen EIP-1193 provider
  const [pickerOpen, setPickerOpen] = useState(false);
  const activeEthRef = useRef(null);
  useEffect(() => { activeEthRef.current = activeEth; }, [activeEth]);

  // The EIP-1193 provider we should currently talk to: the explicitly chosen
  // one, else the legacy window.ethereum.
  const eth = useCallback(() => activeEthRef.current || (typeof window !== 'undefined' ? window.ethereum : null), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onAnnounce = (event) => {
      const detail = event.detail;
      if (!detail || !detail.provider || !detail.info) return;
      setWallets((prev) => {
        // De-dupe by uuid (falls back to rdns/name).
        const key = (d) => d.info.uuid || d.info.rdns || d.info.name;
        if (prev.some((p) => key(p) === key(detail))) return prev;
        return [...prev, { info: detail.info, provider: detail.provider }];
      });
    };
    window.addEventListener('eip6963:announceProvider', onAnnounce);
    // Ask any already-loaded wallets to (re-)announce.
    window.dispatchEvent(new Event('eip6963:requestProvider'));
    return () => window.removeEventListener('eip6963:announceProvider', onAnnounce);
  }, []);

  // Track the current account so listeners can tell a real switch from a same-account
  // event fired on load (which must NOT clear the session).
  const accountRef = useRef(null);
  useEffect(() => { accountRef.current = account; }, [account]);

  // Rebuild provider + signer for whatever account the active wallet exposes.
  const refreshSigner = useCallback(async () => {
    const e = eth();
    if (!e) return null;
    try {
      const browserProvider = new ethers.BrowserProvider(e);
      const walletSigner = await browserProvider.getSigner();
      const network = await browserProvider.getNetwork();
      setProvider(browserProvider);
      setSigner(walletSigner);
      setChainId(Number(network.chainId));
      return walletSigner;
    } catch {
      return null;
    }
  }, [eth]);

  // ─── Silent session restore on refresh ───
  // If a wallet is already authorized for this site, re-attach the account
  // WITHOUT a popup and WITHOUT clearing the saved session token. This keeps the
  // user logged in across page refreshes (the JWT lives in localStorage).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.ethereum) return;
    let alive = true;
    (async () => {
      try {
        const e = window.ethereum;
        const bp = new ethers.BrowserProvider(e);
        const accs = await bp.send('eth_accounts', []); // no prompt
        if (!alive || !accs || accs.length === 0) return;
        const s = await bp.getSigner();
        const net = await bp.getNetwork();
        if (!alive) return;
        setActiveEth(e);
        setProvider(bp);
        setSigner(s);
        setChainId(Number(net.chainId));
        setAccount(accs[0].toLowerCase());
      } catch { /* not authorized yet — user will connect manually */ }
    })();
    return () => { alive = false; };
  }, []);

  // ─── GLOBAL listeners: registered ONCE on mount, so account switches and
  // page refreshes are always caught — not only right after a manual connect. ───
  useEffect(() => {
    if (typeof window === 'undefined' || !window.ethereum) return;

    const handleAccountsChanged = async (accs) => {
      if (!accs || accs.length === 0) {
        // Fully disconnected in the wallet.
        clearUserToken();
        setAccount(null);
        setProvider(null);
        setSigner(null);
        setChainId(null);
        return;
      }

      const newAccount = accs[0].toLowerCase();

      // Same account (e.g. event fired on page load) → keep the session token,
      // just refresh the signer. No re-login needed.
      if (accountRef.current && accountRef.current === newAccount) {
        await refreshSigner();
        return;
      }

      // Genuine switch to a different account → invalidate the old session and
      // require a fresh login for the new wallet.
      clearUserToken();
      await refreshSigner();
      setAccount(newAccount);
    };

    const handleChainChanged = () => {
      // Simplest correct behaviour for a chain switch.
      window.location.reload();
    };

    const e = window.ethereum;
    e.on('accountsChanged', handleAccountsChanged);
    e.on('chainChanged', handleChainChanged);

    return () => {
      e.removeListener('accountsChanged', handleAccountsChanged);
      e.removeListener('chainChanged', handleChainChanged);
    };
  }, [refreshSigner]);

  // Connect to a SPECIFIC EIP-1193 provider (used by the wallet picker and the
  // single-wallet fast path).
  const connectWith = useCallback(async (chosenEth) => {
    const e = chosenEth || eth();
    if (!e) {
      setError('No wallet detected. Please install MetaMask, Trust Wallet, or another Web3 wallet.');
      return null;
    }

    setIsConnecting(true);
    setError(null);

    try {
      const browserProvider = new ethers.BrowserProvider(e);
      const accounts = await browserProvider.send('eth_requestAccounts', []);
      const walletSigner = await browserProvider.getSigner();
      const network = await browserProvider.getNetwork();

      setActiveEth(e);
      setProvider(browserProvider);
      setSigner(walletSigner);
      setAccount(accounts[0].toLowerCase());
      setChainId(Number(network.chainId));

      setIsConnecting(false);
      setPickerOpen(false);
      return accounts[0].toLowerCase();
    } catch (err) {
      setError(err.message || 'Failed to connect wallet');
      setIsConnecting(false);
      return null;
    }
  }, [eth]);

  // Pick a wallet from the discovered list (by uuid) and connect to it.
  const connectWithWallet = useCallback(async (walletEntry) => {
    if (!walletEntry || !walletEntry.provider) return null;
    return connectWith(walletEntry.provider);
  }, [connectWith]);

  const connectWallet = useCallback(async () => {
    // Multiple wallets installed and none chosen yet → let the user pick.
    if (wallets.length > 1 && !activeEthRef.current) {
      setPickerOpen(true);
      return null;
    }
    // Exactly one discovered wallet → use it directly (skip window.ethereum race).
    if (wallets.length === 1) {
      return connectWith(wallets[0].provider);
    }
    // No EIP-6963 wallets discovered → fall back to legacy window.ethereum.
    if (typeof window === 'undefined' || !window.ethereum) {
      setError('No wallet detected. Please install MetaMask, Trust Wallet, or another Web3 wallet.');
      return null;
    }
    return connectWith(window.ethereum);
  }, [wallets, connectWith]);

  const disconnect = useCallback(() => {
    // Clear wallet state AND the session token.
    clearUserToken();
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setChainId(null);
    setActiveEth(null);
    setError(null);
    // NOTE: we do NOT removeAllListeners here anymore — the global listeners in
    // the mount effect must stay alive to catch future connects/switches.
  }, []);

  const signMessage = useCallback(async (message) => {
    if (!signer) throw new Error('Wallet not connected');
    return await signer.signMessage(message);
  }, [signer]);

  // ─── Ensure the wallet is on the expected chain ───
  // Attempts wallet_switchEthereumChain; if the chain isn't known to the wallet
  // (error 4902) it adds it via wallet_addEthereumChain using the config-driven
  // network params, then switches. Nothing is hardcoded — everything comes from
  // SystemConfig (surfaced through /trade/price). Returns true if we end up on
  // the target chain, false if the user rejected or it failed.
  const ensureChain = useCallback(async (targetChainId, netParams = {}) => {
    const e = eth();
    if (!e || !targetChainId) return false;
    const target = Number(targetChainId);
    // Already on the right chain — nothing to do.
    if (Number(chainId) === target) return true;

    const hexChainId = '0x' + target.toString(16);
    try {
      await e.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: hexChainId }],
      });
      // chainChanged listener will reload the page.
      return true;
    } catch (switchErr) {
      // 4902 = chain not added to the wallet yet → add it, then it switches.
      if (switchErr.code === 4902 || switchErr.code === -32603) {
        try {
          await e.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: hexChainId,
              chainName: netParams.networkName || `Chain ${target}`,
              nativeCurrency: {
                name: netParams.nativeCurrencySymbol || 'BNB',
                symbol: netParams.nativeCurrencySymbol || 'BNB',
                decimals: 18,
              },
              rpcUrls: netParams.networkRpcUrl ? [netParams.networkRpcUrl] : [],
              blockExplorerUrls: netParams.networkExplorer ? [netParams.networkExplorer] : [],
            }],
          });
          return true;
        } catch {
          return false;
        }
      }
      // 4001 = user rejected, or any other failure.
      return false;
    }
  }, [chainId, eth]);

  return (
    <WalletContext.Provider value={{
      account,
      provider,
      signer,
      chainId,
      isConnecting,
      error,
      wallets,
      connectWallet,
      connectWith,
      connectWithWallet,
      disconnect,
      signMessage,
      refreshSigner,
      ensureChain,
      openWalletPicker: () => setPickerOpen(true),
    }}>
      {children}
      {pickerOpen && (
        <WalletPicker
          wallets={wallets}
          isConnecting={isConnecting}
          onPick={connectWithWallet}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </WalletContext.Provider>
  );
};

// ─── Inline wallet chooser modal ───
// Shown when more than one wallet is installed so the user decides which to open
// instead of the site auto-opening whichever won the window.ethereum race.
const WalletPicker = ({ wallets, isConnecting, onPick, onClose }) => {
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
  };
  const modal = {
    width: 'min(92vw, 380px)', background: 'var(--bg-card, #16181d)',
    border: '1px solid var(--border, #2a2d36)', borderRadius: 16,
    padding: 20, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  };
  const row = {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    padding: '12px 14px', marginTop: 8, borderRadius: 12,
    border: '1px solid var(--border, #2a2d36)', background: 'var(--bg, #0e0f13)',
    color: 'var(--text, #f5f5f5)', cursor: 'pointer', fontSize: 15, fontWeight: 600,
    textAlign: 'left', transition: 'all 0.15s',
  };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Choose a wallet</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', color: 'var(--text-3, #8a8f98)', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}
            aria-label="Close"
          >×</button>
        </div>
        <p style={{ margin: '0 0 6px', fontSize: 13, color: 'var(--text-3, #8a8f98)' }}>
          Multiple wallets are installed. Pick the one you want to connect with.
        </p>
        {wallets.map((w) => (
          <button
            key={w.info.uuid || w.info.rdns || w.info.name}
            style={row}
            disabled={isConnecting}
            onClick={() => onPick(w)}
          >
            {w.info.icon && (
              <img src={w.info.icon} alt="" width={26} height={26} style={{ borderRadius: 6, flexShrink: 0 }} />
            )}
            <span>{w.info.name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
